<template>
  <b-container>
    <TheTitle>{{ couponType }}優惠券</TheTitle>
    <OverviewList :overviews="overviews" v-stream:select="onOverviewSelect$" :loading="loading">
    </OverviewList>
    <TheListingTitle
      title="優惠券列表"
      :selectedKeywordAndFilters="selectedKeywordAndFilters"
      @reset-click="initialize"
    ></TheListingTitle>
    <section class="table-content">
      <template v-if="isEmptyState && role === 'merchant'">
        <CouponEmptyState></CouponEmptyState>
      </template>
      <template v-else>
        <BaseFilterToolbar @reset="initialize" :loading="loading">
          <div class="d-flex">
            <label>
              <b-form-select class="filter keyword-select" v-model="filter.keywordType">
                <b-form-select-option
                  v-for="option in filteredKeywordSelectOptions"
                  :value="option.value"
                  :key="option.text">
                  {{ option.text }}
                </b-form-select-option>
              </b-form-select>
            </label>
            <label id="keyword-filter">
              <input
                v-model.trim="keyword"
                type="text"
                class="form-control"
                placeholder="關鍵字..."
                aria-label="關鍵字..."
                aria-describedby="button-addon1"
              />
            </label>
          </div>
          <label>
            <b-form-select
              class="filter"
              v-model="filter.categoryTagId"
              @change="onTagChange(true)"
            >
              <b-form-select-option :value="undefined">分類</b-form-select-option>
              <b-form-select-option
                v-for="tag in categoryTagOptions$" :key="tag.id" :value="tag.id"
              >
                {{ tag.name }}
              </b-form-select-option>
            </b-form-select>
          </label>
          <label>
            <b-form-select
              class="filter"
              v-model="filter.promoTagId"
              @change="onTagChange(false)"
            >
              <b-form-select-option :value="undefined">推薦標籤</b-form-select-option>
              <b-form-select-option
                v-for="tag in promoTagOptions$" :key="tag.id" :value="tag.id"
              >
                {{ tag.name }}
              </b-form-select-option>
            </b-form-select>
          </label>
          <label>
            <b-form-select class="filter" v-model="filter.status">
              <b-form-select-option
                v-for="option in filteredStatusSelectOptions"
                :value="option.value"
                :key="option.text"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>
          </label>
          <template v-slot:end>
            <template v-if="role === 'merchant' || role === 'sale'">
              <label class="mx-2 app--mobile-hidden">
                <ButtonAdd @click="$router.push(`${$route.path}/add`)">建立優惠券</ButtonAdd>
              </label>
            </template>
            <transition name="fade" v-else>
              <div class="admin-action" v-show="selectedCoupons.length > 0 && !isMobile">
                <div class="d-flex flex-wrap">
                  <b-dropdown text="更新優惠券" variant="primary" class="dropdown mx-2">
                    <b-dropdown-item
                      @click="prepareAction('BULK_ADD_CATEGORY', '你已加入分類標籤到所選優惠')"
                    >
                      <img class="mr-2" src="@/assets/img/couponupdate/category.svg"/>選擇分類
                    </b-dropdown-item>
                    <b-dropdown-item
                      @click="prepareAction('BULK_REMOVE_CATEGORY', '你已移除分類標籤')"
                    >
                      <img class="mr-2" src="@/assets/img/couponupdate/remove-category.svg"/>移除分類標籤
                    </b-dropdown-item>
                    <b-dropdown-item
                      @click="prepareAction('EDIT_PUBLISH_DUE', '你已更新所選優惠')"
                    >
                      <img class="mr-2" src="@/assets/img/couponupdate/publish-due.svg"/>發佈完結日期
                    </b-dropdown-item>
                    <b-dropdown-item
                      @click="prepareAction('EDIT_EXPIRE', '你已更新所選優惠')"
                    >
                      <img class="mr-2" src="@/assets/img/couponupdate/expire-date.svg"/>優惠完結日期
                    </b-dropdown-item>
                    <div id="tooltip-approve">
                      <div :disabled="!canApproveOrReject">
                        <b-dropdown-item
                          @click="prepareAction('APPROVE', '你已批准所選優惠')"
                        >
                          <img class="mr-2" src="@/assets/img/couponupdate/tick.svg"/>批准
                        </b-dropdown-item>
                      </div>
                      <b-tooltip
                        target="tooltip-approve"
                        triggers="hover"
                        variant="light"
                        v-if="!canApproveOrReject && selectedCoupons.length"
                      >
                        <div class="p-3">所有選擇的優惠券狀態都需要<b>審批中</b>或<b>已暫停</b></div>
                      </b-tooltip>
                    </div>
                    <div id="tooltip-reject">
                      <div :disabled="!canApproveOrReject">
                        <b-dropdown-item
                          @click="prepareAction('REJECT', '你已拒絕所選優惠')"
                        >
                          <img class="mr-2" src="@/assets/img/couponupdate/back.svg"/>拒絕
                        </b-dropdown-item>
                      </div>
                      <b-tooltip
                        target="tooltip-reject"
                        triggers="hover"
                        variant="light"
                        v-if="!canApproveOrReject && selectedCoupons.length"
                      >
                        <div class="p-3">所有選擇的優惠券狀態都需要<b>審批中</b></div>
                      </b-tooltip>
                    </div>
                    <div id="tooltip-unpublish">
                      <div :disabled="!canUnpublish">
                        <b-dropdown-item
                          @click="prepareAction('UNPUBLISH', '你已暫停發佈所選優惠')"
                        >
                          <img class="mr-2" src="@/assets/img/couponupdate/unpublish.svg"/>暫停發佈
                        </b-dropdown-item>
                      </div>
                      <b-tooltip
                        target="tooltip-unpublish"
                        triggers="hover"
                        variant="light"
                        v-if="!canUnpublish && selectedCoupons.length"
                      >
                        <div class="p-3">所有選擇的優惠券狀態都需要<b>已排程/已發佈</b></div>
                      </b-tooltip>
                    </div>
                    <div id="tooltip-remove">
                      <div :disabled="!canRemove">
                        <b-dropdown-item
                          @click="prepareAction('REMOVE', '你已移除所選優惠')"
                        >
                          <img class="mr-2" src="@/assets/img/icon/remove.svg"/>移除
                        </b-dropdown-item>
                      </div>
                      <b-tooltip
                        target="tooltip-remove"
                        triggers="hover"
                        variant="light"
                        v-if="!canRemove && selectedCoupons.length"
                      >
                        <div class="p-3">所有選擇的優惠券狀態不能包括<b>已發佈</b></div>
                      </b-tooltip>
                    </div>
                  </b-dropdown>
                </div>
              </div>
            </transition>
            <BaseButton v-stream:click="exportData$" class="ml-4 app--mobile-hidden">
              匯出
            </BaseButton>
          </template>
          <!-- update coupon on mobile -->
          <template v-slot:mobile-update>
            <div class="d-flex flex-column">
              <div
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem"
                @click="prepareAction('BULK_ADD_CATEGORY', '你已加入分類標籤到所選優惠')"
                @click.stop="$bvModal.hide('mobile-update-coupon')"
                >
                <img class="mr-2"
                  src="@/assets/img/couponupdate/category.svg"
                  style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                />選擇分類
              </div>
              <div
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem"
                @click="prepareAction('BULK_REMOVE_CATEGORY', '你已移除分類標籤')"
                @click.stop="$bvModal.hide('mobile-update-coupon')"
                >
                <img class="mr-2"
                  src="@/assets/img/couponupdate/remove-category.svg"
                  style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                />移除分類標籤
              </div>
              <div
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem"
                @click="prepareAction('EDIT_PUBLISH_DUE', '你已更新所選優惠')"
                @click.stop="$bvModal.hide('mobile-update-coupon')"
              >
                <img class="mr-2"
                  src="@/assets/img/couponupdate/publish-due.svg"
                  style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                />發佈完結日期
              </div>
              <div
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem"
                @click="prepareAction('EDIT_EXPIRE', '你已更新所選優惠')"
                @click.stop="$bvModal.hide('mobile-update-coupon')"
              >
                <img class="mr-2"
                  src="@/assets/img/couponupdate/expire-date.svg"
                  style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                  />優惠完結日期
              </div>
              <div id="tooltip-approve"
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem;
                padding-bottom: 0.5rem"
              >
                <div
                  :disabled="!canApproveOrReject"
                  @click="prepareAction('APPROVE', '你已批准所選優惠')"
                  @click.stop="$bvModal.hide('mobile-update-coupon')"
                >
                  <img class="mr-2"
                    src="@/assets/img/couponupdate/tick.svg"
                    style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                  />批准
                </div>
              </div>
              <div id="tooltip-reject"
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem"
              >
                <div
                  :disabled="!canApproveOrReject"
                  @click="prepareAction('REJECT', '你已拒絕所選優惠')"
                  @click.stop="$bvModal.hide('mobile-update-coupon')"
                >
                  <img class="mr-2"
                    src="@/assets/img/couponupdate/back.svg"
                    style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                  />拒絕
                </div>
              </div>
              <div
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem"
              >
                <div
                  :disabled="!canUnpublish"
                  @click="prepareAction('UNPUBLISH', '你已暫停發佈所選優惠')"
                  @click.stop="$bvModal.hide('mobile-update-coupon')"
                >
                  <img class="mr-2"
                    src="@/assets/img/couponupdate/unpublish.svg"
                    style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                  />暫停發佈
                </div>
              </div>
              <div
                id="tooltip-remove"
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem"
              >
                <div
                  :disabled="!canRemove"
                  @click="prepareAction('REMOVE', '你已移除所選優惠')"
                  @click.stop="$bvModal.hide('mobile-update-coupon')"
                >
                  <img class="mr-2"
                    src="@/assets/img/icon/remove.svg"
                    style="padding-bottom: 0.2rem; padding-left: 0.75rem;"
                  />移除
                </div>
              </div>
              <div
                style="border-top: 0.1rem solid #D3D3D3;
                padding-top: 0.5rem; padding-bottom: 0.5rem; padding-left: 2.5rem"
                @click="$bvModal.hide('mobile-update-coupon')"
              >
                取消
              </div>
            </div>
          </template>
        </BaseFilterToolbar>
        <DataTable
          v-show="list"
          :fields="fields"
          :mobileFields="mobileFields"
          :items="list"
          :total-result="total"
          :options.sync="options"
          :loading="loading"
          @row-clicked="goToItem"
          v-stream:row-selected="onRowSelected$"
          selectable
          ref="dataTable"
          v-stream:export-click="exportData$"
          :mobile-update="
            (this.role === 'admin' || this.role === 'sale') && selectedCoupons.length > 0
          "
          mobile-searchable
          mobile-exportable
          showRowDetails
        >
          <template v-slot:cell(status)="{ value }">
            <StatusLabel :item="value"></StatusLabel>
          </template>
          <template v-slot:cell(name1)="{ item }">
            <div class="d-flex">
              <img v-if="item.cover" class="data-table__cover mr-2" :src="item.cover" />
              <div>
                {{ item.name1 }}
                <br />
                <div class="text-secondary">{{ item.tags }}</div>
              </div>
            </div>
          </template>
          <!-- mobile only -->
          <template v-slot:id="{ item }">
            <router-link :to="{ path: `${$route.path}/${item.id}` }"> {{ item.id }}</router-link>
          </template>
          <!-- end of mobile only -->
        </DataTable>
        <b-skeleton-table v-if="!list" :rows="15" :columns="5"></b-skeleton-table>
      </template>
      <ValidationObserver ref="form" v-slot="{ validate }">
        <AdminActionConfirmModal
          v-model="approval"
          :categoryTagOptions="categoryTagOptions$"
          :promoTagOptions="promoTagOptions$"
          :onCouponStatusUpdate="onCouponStatusUpdate$"
          :loading="loading"
          :validate="validate"
          :selectedExpireDates="getSelectedExpireDates()"
          :selectedPublishOns="getSelectedPublishOns()"
          :selectedPublishDues="getSelectedPublishDues()"
          :selectedTagIds="getSelectedTagIds()"
        ></AdminActionConfirmModal>
      </ValidationObserver>
      <ActionSuccessModal
        v-model="actionSuccessModal"
        v-stream:hide="onActionSuccessModalClose$"
      ></ActionSuccessModal>
    </section>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { scheduled, asyncScheduler } from 'rxjs';
import {
  tap, map, switchMap, debounceTime, pluck, mergeAll,
} from 'rxjs/operators';
import {
  BFormSelect,
  BFormSelectOption,
  BContainer,
  BSkeletonTable,
  BTooltip,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue';
import CouponEmptyState from '@/components/coupon/CouponEmptyState.vue';
import AdminActionConfirmModal from '@/components/coupon/AdminActionConfirmModal.vue';
import ActionSuccessModal from '@/components/ActionSuccessModal.vue';
import BaseButton from '@/components/BaseButton.vue';
import axios from 'axios';
import {
  ButtonAdd,
} from '../../components/button';
import dataTableMixin from '../../mixins/data-table';
import exportMixin from '../../mixins/export';
import filtersQueryStringMixin from '../../mixins/filtersQueryString';
import optionsQueryStringMixin from '../../mixins/optionsQueryString';
import http from '../../helpers/http';
import getCouponStatus from '../../helpers/coupon-status';
import TheTitle from '../../components/TheTitle.vue';
import TheListingTitle from '../../components/TheListingTitle.vue';
import OverviewList from '../../components/overview/OverviewList.vue';
import DataTable from '../../components/DataTable.vue';
import StatusLabel from '../../components/StatusLabel.vue';
import BaseFilterToolbar from '../../components/BaseFilterToolbar.vue';
import isMobileMixin from '../../mixins/is-mobile';
import formMixin from '../../mixins/form';

const formatQuantity = (value, key, { quantity, draftQuantity }) => {
  if (draftQuantity !== null) {
    return draftQuantity || '不限';
  }
  return quantity || '不限';
};

const COUPON_HEADERS = [
  { key: 'id', label: '參考編號', sortable: true },
  { key: 'name1', label: '優惠名稱', sortable: true },
  { key: 'merchant', label: '商戶名稱', sortable: true },
  { key: 'brandName', label: '品牌名稱', sortable: true },
  {
    key: 'status',
    label: '狀態',
    sortable: true,
    formatter: (value, key, item) => getCouponStatus(item.status),
  },
  { key: 'publishOn', label: '發佈日期', sortable: true },
  { key: 'expire', label: '到期日', sortable: true },
  { key: 'cost', label: 'UFUN', sortable: true },
  { key: 'redeemed', label: '已兌換數量', sortable: true },
  {
    key: 'quantity',
    label: '總供應數量',
    sortable: true,
    formatter: formatQuantity,
  },
  { key: 'createdAt', label: '創建時間', sortable: true },
];

const PAYMENT_COUPON_HEADERS = [
  { key: 'id', label: '參考編號', sortable: true },
  { key: 'name1', label: '優惠名稱', sortable: true },
  { key: 'merchant', label: '商戶名稱', sortable: true },
  { key: 'brandName', label: '品牌名稱', sortable: true },
  {
    key: 'status',
    label: '狀態',
    sortable: true,
    formatter: (value, key, item) => getCouponStatus(item.status),
  },
  { key: 'publishOn', label: '發佈日期', sortable: true },
  { key: 'expire', label: '到期日', sortable: true },
  {
    key: 'price',
    label: '售價',
    sortable: true,
  },
  { key: 'redeemed', label: '已售出數量', sortable: true },
  {
    key: 'quantity',
    label: '總供應數量',
    sortable: true,
    formatter: formatQuantity,
  },
  { key: 'createdAt', label: '創建時間', sortable: true },
];

const MOBILE_HEADER = [
  {
    key: 'name1',
    label: '優惠名稱',
  },
  {
    key: 'status',
    formatter: (value, key, item) => getCouponStatus(item.status),
    thStyle: {
      display: 'none',
    },
  },
];

export default {
  mixins: [dataTableMixin, exportMixin, filtersQueryStringMixin, optionsQueryStringMixin,
    isMobileMixin, formMixin],
  components: {
    AdminActionConfirmModal,
    ActionSuccessModal,
    BFormSelect,
    BFormSelectOption,
    BContainer,
    BSkeletonTable,
    TheTitle,
    TheListingTitle,
    OverviewList,
    DataTable,
    StatusLabel,
    CouponEmptyState,
    BaseButton,
    ButtonAdd,
    BaseFilterToolbar,
    BTooltip,
    'b-dropdown-item': BDropdownItem,
    BDropdown,
  },
  data: () => ({
    filter: {
      keywordType: 'all',
      status: undefined,
      categoryTagId: undefined,
      promoTagId: undefined,
    },
    options: {
      sortBy: 'id',
    },
    defaultOrder: 'desc',
    defaultSortBy: 'id',
    // defaultKeywordType: 'all',
    keyword: '',
    keywordSelectOptions: [
      {
        value: 'all',
        text: '所有關鍵字',
      },
      {
        value: 'id',
        text: '參考編號',
      },
      {
        value: 'name',
        text: '優惠名稱',
      },
      {
        value: 'merchant',
        text: '商戶名稱',
        roles: ['admin', 'sale'],
      },
      {
        value: 'merchantId',
        text: '商戶編號',
        roles: ['admin', 'sale'],
      },
      {
        value: 'brandId',
        text: '品牌名稱',
        roles: ['admin', 'sale'],
      },
    ],
    statusSelectOptions: [
      { value: undefined, text: '所有狀態' },
      { value: 'PENDING', text: '審批中' },
      { value: 'PUBLISHED', text: '已發佈' },
      { value: 'SCHEDULED', text: '已排程' },
      { value: 'STOPPED', text: '已暫停' },
      { value: 'REJECTED', text: '已拒絕' },
      { value: 'EXPIRED', text: '已過期' },
      { value: 'DRAFT', text: '草稿', merchantOnly: true },
    ],
    selectedCoupons: [],
    selectedTags: [],
    approval: {
      modal: false,
      action: null,
      remarks: null,
      categoryTags: [],
      promoTags: [],
      publishDue: null,
      expire: null,
    },
    actionSuccessModal: {
      show: false,
      action: null,
      msg: null,
    },
    mobileFields: MOBILE_HEADER,
  }),
  computed: {
    ...mapGetters(['role']),
    couponType() {
      return this.$route.meta.paymentType === 'UFUN' ? '積分' : '付費';
    },
    fields() {
      let couponHeaders = COUPON_HEADERS;
      let paymentCouponHeaders = PAYMENT_COUPON_HEADERS;
      if (this.role === 'admin' || this.role === 'sale') {
        couponHeaders = [
          ...COUPON_HEADERS,
          { key: 'updatedAt', label: '更新時間', sortable: true },
        ];
        paymentCouponHeaders = [
          ...PAYMENT_COUPON_HEADERS,
          { key: 'updatedAt', label: '更新時間', sortable: true },
        ];
      }
      return this.$route.name === 'ListCoupons' ? couponHeaders : paymentCouponHeaders;
    },
    overviews() {
      return this.result?.overviews.result;
    },
    isEmptyState() {
      return this.result?.overviews.total === 0;
    },
    list() {
      return this.result?.list.result;
    },
    canApproveOrReject() {
      return (
        this.selectedCoupons.length > 0
        && this.selectedCoupons.every(
          (selectedCoupon) => selectedCoupon.status === 'PENDING' || selectedCoupon.status === 'STOPPED',
        )
      );
    },
    canUnpublish() {
      return (
        this.selectedCoupons.length > 0
        && this.selectedCoupons.every(
          (selectedCoupon) => selectedCoupon.status === 'PUBLISHED' || selectedCoupon.status === 'SCHEDULED',
        )
      );
    },
    canRemove() {
      return this.selectedCoupons.every((selectedCoupon) => selectedCoupon.published === 0);
    },
  },
  domStreams: [
    'onOverviewSelect$',
    'onRowSelected$',
    'onCouponStatusUpdate$',
    'onActionSuccessModalClose$',
    'exportData$',
  ],
  subscriptions() {
    const getData$ = (data) => this.httpRequest$(
      {
        method: 'GET',
        url: `/agc-${this.role}-api/coupons?paymentType=${this.$route.meta.paymentType}`,
      },
      data,
    );

    return {
      result: scheduled(
        [...this.scheduleTasks, this.onActionSuccessModalClose$],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        tap(() => {
          this.loading = true;
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        pluck('data'),
        switchMap(getData$),
      ),
      exportData: this.exportData$.pipe(
        switchMap(() => axios({
          method: 'GET',
          url: `/agc-${this.role}-api/export/coupons?paymentType=${this.$route.meta.paymentType}`,
          params: {
            ...this.filter,
            q: this.keyword,
            selectedItem: this.selectedCoupons.map((item) => item.id).join(','),
          },
          responseType: 'blob',
        }).then((res) => {
          this.downloadFile(
            res.data,
            this.generateFileName(
              `${this.$route.meta.paymentType === 'UFUN' ? '' : 'paid-'}coupon-list-data`,
            ),
          );
        })),
      ),
      onOverviewSelect: this.onOverviewSelect$.pipe(
        pluck('event', 'msg'),
        tap((status) => {
          this.filter.status = this.filter.status === status ? undefined : status;
        }),
      ),
      onRowSelected: this.onRowSelected$.pipe(
        pluck('event', 'msg'),
        tap((selectedCoupons) => {
          this.selectedCoupons = selectedCoupons;
        }),
      ),
      couponStatusUpdate: this.onCouponStatusUpdate$.pipe(
        switchMap(this.validate),
        tap(() => {
          this.loading = true;
        }),
        switchMap(() => {
          const {
            action, remarks, msg, categoryTags, promoTags, publishDue, expire,
          } = this.approval;
          const data = {
            rewardIds: this.selectedCoupons.map((selectedCoupon) => selectedCoupon.id),
          };
          let actionType = action;
          if (action === 'BULK_ADD_CATEGORY' || action === 'BULK_REMOVE_CATEGORY') {
            actionType = action.split('_').join('');
            data.tags = [...categoryTags, ...promoTags];
          } else if (action === 'EDIT_PUBLISH_DUE') {
            actionType = action.split('_').join('');
            data.publishDue = publishDue;
          } else if (action === 'EDIT_EXPIRE') {
            actionType = action.split('_').join('');
            data.expire = expire;
          } else {
            data.remarks = remarks;
          }
          return http(
            {
              method: 'POST',
              url: `/agc-${this.role}-api/coupons/${actionType.toLowerCase()}`,
              data,
            },
            {
              next: () => {
                if (msg) {
                  this.actionSuccessModal = {
                    ...this.actionSuccessModal,
                    show: true,
                    action,
                    msg,
                  };
                }
              },
              error: ({ data: { msg: errorMsg } }) => {
                this.actionSuccessModal = {
                  ...this.actionSuccessModal,
                  show: true,
                  msg: errorMsg,
                };
              },
              complete: () => {
                this.approval.modal = false;
                this.loading = false;
              },
            },
          );
        }),
      ),
      categoryTagOptions$: http({
        method: 'GET',
        url: `/agc-public-api/tags?type=category&isAdmin=${this.role === 'admin'}`,
      }).pipe(
        pluck('result'),
        map((result) => result.map(({ id, name }) => ({ id, name }))),
      ),
      promoTagOptions$: http({
        method: 'GET',
        url: `/agc-public-api/tags?type=promo&isAdmin=${this.role === 'admin'}`,
      }).pipe(
        pluck('result'),
        map((result) => result.map(({ id, name }) => ({ id, name }))),
      ),
    };
  },
  methods: {
    goToItem({ id }) {
      this.$router.push({
        path: `${this.$route.path}/${id}`,
      });
    },
    onOverviewItemClicked(status) {
      this.filter.status = status.toUpperCase();
    },
    onTagChange(isCategoryTag) {
      // When categoryTag is selected, promoTag filter is canceled and vice versa
      if (isCategoryTag && this.filter.promoTagId) {
        this.filter.promoTagId = undefined;
      } else if (!isCategoryTag && this.filter.categoryTagId) {
        this.filter.categoryTagId = undefined;
      }
    },
    initialize() {
      this.filter.keywordType = 'all';
      this.keyword = '';
      this.filter.status = undefined;
      this.filter.categoryTagId = undefined;
      this.filter.promoTagId = undefined;
      setTimeout(() => {
        this.loading = false;
      }, 550);
      // when user updates filter too fast, the loading state will not set back to false
      // this time must be bigger than debounceTime's time
    },
    prepareAction(action, msg) {
      this.approval = {
        modal: true,
        action,
        remarks: null,
        msg,
        categoryTags: [],
        promoTags: [],
        publishDue: null,
        expire: null,
      };
    },
    getCouponStatus,
    getSelectedExpireDates() {
      return this.selectedCoupons.map((selectedCoupon) => selectedCoupon.expire);
    },
    getSelectedPublishOns() {
      return this.selectedCoupons.map((selectedCoupon) => selectedCoupon.publishOn);
    },
    getSelectedPublishDues() {
      return this.selectedCoupons.map((selectedCoupon) => selectedCoupon.publishDue);
    },
    getSelectedTagIds() {
      const categoryTagIds = this.categoryTagOptions$
        ? this.categoryTagOptions$.map((option) => String(option.id))
        : [];
      return this.selectedCoupons.map((selectedCoupon) => {
        const tagIdArray = String(selectedCoupon.tagIds).replaceAll(' ', '').split(',');
        const filteredTagIds = tagIdArray.filter((tagId) => categoryTagIds.includes(tagId));
        return filteredTagIds.join(', ');
      });
    },
  },
  created() {
    if (this.role === 'admin' || this.role === 'sale') {
      const { keywordType, q } = this.$route.query;
      this.options.sortBy = 'updatedAt';
      this.defaultSortBy = 'updatedAt';
      if (keywordType && q) {
        this.filter.keywordType = keywordType;
        this.keyword = q;
        this.$router.replace(`/${this.role}/coupons`);
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    // router error if added below code
    // this.initialize();
    next();
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/data-table/list-table-content";
@import "../../assets/scss/transition/fade";

div.admin-action {
  height: 100%;
}

::v-deep{
  .dropdown {
    .btn-primary{
      padding-top: 8px;
      padding-bottom: 8px;
    }
  }
}
</style>
